/** Review state of each open decision, derived from the draft: Adjusted (any leaf differs from the seed), Maintained (unmoved and marked), else To review. Pure: no React. */
import type { ContentCatalog, DraftView } from "@bsim/contracts";
import { decisionOfPath, decisionPaths, type DecisionFamily, type DecisionId } from "@bsim/domain";
import { deskGroups } from "./deskSheet";

export type ReviewState = "adjusted" | "maintained" | "toReview";
type Catalog = Pick<ContentCatalog, "decisions" | "policyDecisions">;

/** The decisions with at least one leaf that differs from the seed (composites included). */
const adjustedSet = (draft: DraftView): ReadonlySet<string> => new Set(decisionPaths(draft.decisions, draft.seedDecisions).map(decisionOfPath));

const stateIn = (adjusted: ReadonlySet<string>, marked: ReadonlySet<string>, id: DecisionId): ReviewState =>
  adjusted.has(id) ? "adjusted" : marked.has(id) ? "maintained" : "toReview";

const markedSet = (draft: DraftView): ReadonlySet<string> => new Set(draft.reviewedDecisionIds ?? []);

/** Adjusted if any leaf differs from draft.seedDecisions; else Maintained if the draft's marks name it; else toReview. An id outside editableDecisionIds is judged by the same rule. */
export function decisionReviewState(draft: DraftView, decisionId: DecisionId): ReviewState {
  return stateIn(adjustedSet(draft), markedSet(draft), decisionId);
}

/** The open decisions of one desk, in the sheet's row order. */
export function openDecisionsOf(draft: DraftView, desk: DecisionFamily, catalog: Catalog): readonly DecisionId[] {
  const open = new Set<string>(draft.editableDecisionIds);
  return deskGroups(catalog, desk).flatMap((g) => g.rows.map((r) => r.id)).filter((id): id is DecisionId => open.has(id));
}

export interface DeskReview { readonly desk: DecisionFamily; readonly decisionIds: readonly DecisionId[]; readonly adjusted: number; readonly maintained: number; readonly toReview: number }
export interface ReviewTotals { readonly desks: readonly DeskReview[]; readonly adjusted: number; readonly maintained: number; readonly toReview: number; readonly total: number }

/** Per desk (only desks with an open decision, in the order given) and in total. */
export function reviewTotals(draft: DraftView, desks: readonly DecisionFamily[], catalog: Catalog): ReviewTotals {
  const adjusted = adjustedSet(draft), marked = markedSet(draft);
  const rows = desks.flatMap((desk): DeskReview[] => {
    const decisionIds = openDecisionsOf(draft, desk, catalog);
    if (decisionIds.length === 0) return [];
    const states = decisionIds.map((id) => stateIn(adjusted, marked, id));
    const count = (s: ReviewState): number => states.filter((x) => x === s).length;
    return [{ desk, decisionIds, adjusted: count("adjusted"), maintained: count("maintained"), toReview: count("toReview") }];
  });
  const sum = (pick: (d: DeskReview) => number): number => rows.reduce((n, d) => n + pick(d), 0);
  return { desks: rows, adjusted: sum((d) => d.adjusted), maintained: sum((d) => d.maintained), toReview: sum((d) => d.toReview), total: sum((d) => d.decisionIds.length) };
}

/** The first To-review decision walking `desks` in order, or null when none is left. */
export function firstToReview(draft: DraftView, desks: readonly DecisionFamily[], catalog: Catalog): DecisionId | null {
  const adjusted = adjustedSet(draft), marked = markedSet(draft);
  for (const desk of desks) {
    const hit = openDecisionsOf(draft, desk, catalog).find((id) => stateIn(adjusted, marked, id) === "toReview");
    if (hit !== undefined) return hit;
  }
  return null;
}
